import React, { createContext, useCallback, useContext, useState } from "react";

import { cn } from "../lib/utils";

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

interface SelectContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  items: Map<string, string>; // value → label
  registerItem: (value: string, label: string) => void;
}

const SelectContext = createContext<SelectContextValue>({
  items: new Map(),
  registerItem: () => {},
});

/* ------------------------------------------------------------------ */
/* Select (root)                                                      */
/* ------------------------------------------------------------------ */

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}

export function Select({
  value,
  defaultValue,
  onValueChange,
  children,
}: SelectProps) {
  const [items, setItems] = useState<Map<string, string>>(new Map());

  const registerItem = useCallback((val: string, label: string) => {
    setItems((prev) => {
      if (prev.get(val) === label) return prev;
      const next = new Map(prev);
      next.set(val, label);
      return next;
    });
  }, []);

  return (
    <SelectContext.Provider
      value={{
        value: value ?? defaultValue,
        onValueChange,
        items,
        registerItem,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* SelectTrigger                                                      */
/* ------------------------------------------------------------------ */

export function SelectTrigger({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const { value, onValueChange, items } = useContext(SelectContext);

  return (
    <select
      value={value ?? ""}
      onChange={(e) => onValueChange?.(e.target.value)}
      className={cn(
        "border-input ring-offset-background flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-sm",
        "focus:ring-ring focus:ring-1 focus:outline-none",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      {Array.from(items.entries()).map(([val, label]) => (
        <option key={val} value={val}>
          {label}
        </option>
      ))}
    </select>
  );
}

/* ------------------------------------------------------------------ */
/* SelectValue – no-op (native <select> handles display)              */
/* ------------------------------------------------------------------ */

export function SelectValue(_props: {
  children?: React.ReactNode;
  placeholder?: string;
}) {
  return null;
}

/* ------------------------------------------------------------------ */
/* SelectContent – renders children so SelectItems can register        */
/* ------------------------------------------------------------------ */

export function SelectContent({
  children,
  align,
  side,
}: {
  children: React.ReactNode;
  align?: string;
  side?: string;
}) {
  return <>{children}</>;
}

/* ------------------------------------------------------------------ */
/* SelectItem – registers with Select context on mount                 */
/* ------------------------------------------------------------------ */

export function SelectItem({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}) {
  const { registerItem } = useContext(SelectContext);

  const label =
    typeof children === "string"
      ? children
      : React.Children.toArray(children)
          .map((child) => (typeof child === "string" ? child : ""))
          .join("");

  React.useEffect(() => {
    registerItem(value, label);
  }, [value, label, registerItem]);

  return null;
}
